import { type DisplayStatusType } from '@constants'
import { Collapse } from '@fortune-ui'
import { type WpOrderStatus, type WpOrderType } from '@hooks-api/types/dataModel/eCommerceOrders'
import { OrderAction } from '@hooks-api/types/dataModel/wp'
import { RefundStatusBadge } from '@order-detail/components/shared/Orders/RefundStatusBadge'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Chip } from '@pets-ui/Chip/Chip'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownSmall, ArrowUpSmall, Exchange } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { wordPressNavigate } from '@utils'
import { type RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextWpMyAccount } from '../useContextWpMyAccount'
import { getOrderCardUiRules } from '../utils/order-card-ui-rules'
import { type OrderListDTO } from '../utils/transformOrderDataDTO'
import { OrderReceipt } from './OrderReceipt'
import { OrderTable } from './OrderTable'
import { OrderThumbnail } from './OrderThumbnail'

interface OrderCardProps {
  order: OrderListDTO
  type: WpOrderType
  status: WpOrderStatus
  isFocused: boolean
  focusedOrderRef?: RefObject<HTMLDivElement | null>
  isMutatingGetPaymentTransaction: boolean
  onClickOrderAction: (orderId: string, actionType: OrderAction, paymentTransactionId?: string | null, transactionHash?: string, displayStatus?: DisplayStatusType) => void
  onClickTransactionOrderDetail: (transactionId?: string | null) => void
  getOrderDetailUrl: (isTransaction: boolean, orderId: string, transactionId: string) => string
  onOpenCancelSubscriptionDialog: (params: { fortuneOrderId: string }) => void
  onOpenEditPaymentMethod: (orderId: string, fortuneOrderId: string, paymentTokenId: number | null) => void
  onSendCancelSubscriptionEvent: () => void
}

const styles = stylex.create({
  orderDetailButton: {
    alignSelf: 'flex-end',
    height: '36px',
  },
  orderTitle: {
    overflow: 'hidden',
    color: colors.grey900,
    fontWeight: 600,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  estimatedShip: {
    flexShrink: 0,
  },
  focusedContainer: {
    borderColor: colors.yellow400,
    borderStyle: 'solid',
    borderWidth: 1,
    scrollMarginTop: '72px',
  },
})

export function OrderCard({
  order,
  type,
  status,
  isFocused,
  focusedOrderRef,
  isMutatingGetPaymentTransaction,
  onClickOrderAction,
  onClickTransactionOrderDetail,
  getOrderDetailUrl,
  onOpenCancelSubscriptionDialog,
  onOpenEditPaymentMethod,
  onSendCancelSubscriptionEvent,
}: OrderCardProps) {
  const { t } = useTranslation()
  const { environment } = useContextWpMyAccount()
  const isMerchant = environment === 'merchant'
  const isWordPressTablet = useResponsive('wordPressTablet')
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    images,
    orderId,
    title,
    subtitle,
    displayNextDeliveryQuantity,
    displayNextDeliveryDate,
    estimatedDeliveryDate,
    transactionId,
    chargeAt,
    fortuneOrderId,
    transactionHash,
    orderStatus,
    paymentTokenId,
    creditCardLast4,
    refundStatus,
    displayStatus,
  } = order

  const {
    isTransaction,
    isWarehouseOrderOrSubscriptionOrder,
    shouldShowCancelOrder,
    shouldShowCancelSubscriptionButton,
    shouldShowPaymentCardChip,
    shouldShowPreOrderBadge,
    shouldShowRecurringPaymentDate,
    shouldShowSubscriptionEstimatedDate,
    shouldShowRefundStatus,
    shouldShowRepeatButton,
    shouldShowShippingSetting,
    shouldShowTabletDeliveryInfo,
    shouldShowTabletPaymentFailed,
    shouldShowMobileDeliveryInfo,
    shouldShowMobilePaymentFailed,
    shouldShowPayButton,
  } = getOrderCardUiRules({ order, status, type })

  const handleClickOrderDetail = (orderId?: string | null) => {
    if (!orderId) {
      console.error('orderId is null or undefined!')
      return
    }
    wordPressNavigate(`/my-account/view-order/${orderId}/`)
  }

  const handleExpandOrder = () => {
    setIsExpanded(previous => !previous)
  }

  const handleCancelSubscription = () => {
    if (!fortuneOrderId) return
    onOpenCancelSubscriptionDialog({ fortuneOrderId })
    onSendCancelSubscriptionEvent()
  }

  const handleMobileCancelSubscription = () => {
    if (!fortuneOrderId) return
    onOpenCancelSubscriptionDialog({ fortuneOrderId })
  }

  const handleEditPaymentCard = () => {
    if (!fortuneOrderId || paymentTokenId == null) return
    onOpenEditPaymentMethod(orderId, fortuneOrderId, paymentTokenId)
  }

  // ── 共用 UI 區塊 ──
  const paymentCardChipElement = shouldShowPaymentCardChip && (
    <Chip color="blueGrey" variant="square" size="small" onClick={handleEditPaymentCard}>
      <HStack gap={2} alignItems="center">
        <Text variant="caption">
          {t('wpOrder.orderPaymentCard', { last4: creditCardLast4, defaultValue: '扣款卡號：結尾為 *{{last4}}' })}
        </Text>
        <Exchange fontSize={16} />
      </HStack>
    </Chip>
  )

  const payButtonElement = shouldShowPayButton && (
    <Button
      color="primary"
      onClick={() => onClickOrderAction(orderId, OrderAction.PAY, transactionId, transactionHash)}
    >
      {t('wpOrder.promptPayment')}
    </Button>
  )

  const repeatButtonElement = shouldShowRepeatButton && (
    <Button
      color="primary"
      rootStyle={styles.orderDetailButton}
      onClick={() => onClickOrderAction(orderId, OrderAction.REPEAT, transactionId, transactionHash, displayStatus)}
      loading={isMutatingGetPaymentTransaction}
    >
      {t('wpOrder.repeatOrder', { defaultValue: '再買一次' })}
    </Button>
  )

  const shippingSettingsElement = shouldShowShippingSetting && (
    <>
      <Divider my={2} />
      <Box cursor="pointer" color={colors.grey600}>
        <HStack
          py={1}
          gap={2}
          justifyContent="center"
          onClick={handleExpandOrder}
        >
          <Text variant="caption">{t('wpOrder.deliverySetting', { defaultValue: '配送設定' })}</Text>
          {isExpanded ? <ArrowUpSmall /> : <ArrowDownSmall />}
        </HStack>
      </Box>
      <Collapse in={isExpanded}>
        <OrderTable status={status} orderList={order} type={type} warehouseOrSubscriptionOrderStatus={isWarehouseOrderOrSubscriptionOrder ? orderStatus : undefined} />
      </Collapse>
    </>
  )

  if (isWordPressTablet) {
    return (
      <Box rootRef={isFocused ? focusedOrderRef : undefined} rootStyle={[isFocused && styles.focusedContainer]} backgroundColor={colors.white} borderRadius={8} mt={2} py={2.5} px={3}>
        <VStack>
          <HStack flexWrap="nowrap" gap={2}>
            <Box width="76px" height="76px" minHeight="76px" minWidth="76px" overflow="hidden" display="flex" alignItems="center" justifyContent="center">
              <OrderThumbnail images={images} />
            </Box>
            <VStack width="100%" gap={2} maxWidth="732px">
              <HStack gap={2} alignItems="flex-start" justifyContent="space-between" flexWrap="nowrap">
                <HStack gap={2} alignItems="center" minWidth={0} flexWrap="nowrap">
                  <Text rootStyle={styles.orderTitle} variant="subtitle">{title}</Text>
                  {subtitle && <Text noWrap variant="caption" color={colors.grey600}>{subtitle}</Text>}
                </HStack>
                {shouldShowCancelOrder
                && (
                  <Button
                    variant="text"
                    color="blue"
                    onClick={() => onClickOrderAction(orderId, OrderAction.CANCEL, transactionId, transactionHash, displayStatus)}
                  >
                    <Text noWrap>{t('wpOrder.cancelOrder')}</Text>
                  </Button>
                )}
                {shouldShowCancelSubscriptionButton && (
                  <Button variant="text" color="blue" onClick={handleCancelSubscription}>
                    <Text noWrap>{t('wpOrder.cancelAutoRenewButton', { defaultValue: '取消自動購買' })}</Text>
                  </Button>
                )}
              </HStack>
              <OrderReceipt order={order} status={status} type={type} />
            </VStack>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" flexWrap="nowrap">
            <HStack gap={1} width="100%" alignItems="center">
              <Box>
                {shouldShowTabletDeliveryInfo && <Text noWrap rootStyle={styles.estimatedShip} color={colors.grey600}>{t('wpOrder.estimatedDeliveryDateAndQuantity', { estimatedDeliveryDate: displayNextDeliveryDate, nextDeliveryQuantity: displayNextDeliveryQuantity })}</Text>}
                {shouldShowSubscriptionEstimatedDate && <Text noWrap color={colors.grey600}>{t('wpSubscription.estimatedDate', { estimatedDate: estimatedDeliveryDate })}</Text>}
                {shouldShowRecurringPaymentDate && <Text noWrap rootStyle={styles.estimatedShip} color={colors.grey600}>{t('wpOrder.recurringPayment', { recurringPaymentDate: chargeAt })}</Text>}
                {shouldShowTabletPaymentFailed && <Text noWrap color={colors.red400}>{t('wpOrder.PaymentFailedDescription')}</Text>}
                {shouldShowPreOrderBadge && <Text noWrap color={colors.primary600}>{t('wpOrder.preOrder')}</Text>}
              </Box>
              {paymentCardChipElement}
              {shouldShowRefundStatus && <RefundStatusBadge refundStatus={refundStatus} isMerchant={isMerchant} />}
            </HStack>
            <HStack gap={2} width="100%" justifyContent="flex-end">
              {transactionId && (
                <Button
                  component="a"
                  href={getOrderDetailUrl(isTransaction, orderId, transactionId)}
                  variant="outlined"
                  color="grey"
                  rootStyle={styles.orderDetailButton}
                  onClick={(e) => {
                    e.preventDefault()
                    if (isTransaction) {
                      onClickTransactionOrderDetail(transactionId)
                    }
                    else {
                      handleClickOrderDetail(orderId)
                    }
                  }}
                >
                  {t('wpOrder.orderDetail')}
                </Button>
              )}
              {payButtonElement}
              {/* NOTE: campaign 再買一次目前因 store 還沒處理，先隱藏 */}
              { environment !== 'campaign' && repeatButtonElement}
            </HStack>
          </HStack>
        </VStack>

        {shippingSettingsElement}
      </Box>
    )
  }

  return (
    <Box rootRef={isFocused ? focusedOrderRef : undefined} rootStyle={[isFocused && styles.focusedContainer]} backgroundColor={colors.white} borderRadius={8} mt={2} px={2.5} p={3}>
      <VStack flexWrap="nowrap">
        <HStack flexWrap="nowrap" gap={2} justifyContent="space-between">
          <Text rootStyle={styles.orderTitle}>{title}</Text>
          {shouldShowCancelOrder && <Button variant="text" color="blue" onClick={() => onClickOrderAction(orderId, OrderAction.CANCEL, transactionId, transactionHash, displayStatus)}><Text noWrap>{t('wpOrder.cancelOrder')}</Text></Button>}
          {shouldShowCancelSubscriptionButton && <Button variant="text" color="blue" onClick={handleMobileCancelSubscription}><Text noWrap>{t('wpOrder.cancelAutoRenewButton', { defaultValue: '取消自動購買' })}</Text></Button>}
        </HStack>
        {isWarehouseOrderOrSubscriptionOrder && <Text variant="caption" color={colors.grey600}>{subtitle}</Text>}
        <HStack gap={2} mt={2} flexWrap="nowrap" width="100%">
          <Box minWidth="116px" minHeight="116px" width="116px" height="116px" overflow="hidden" display="flex" alignItems="center" justifyContent="center">
            <OrderThumbnail images={images} />
          </Box>
          <OrderReceipt order={order} status={status} type={type} />
        </HStack>
        <HStack mt={1} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
          <HStack gap={0.5} flexShrink={1}>
            {shouldShowMobileDeliveryInfo && <Text color={colors.grey600}>{t('wpOrder.estimatedDeliveryDateAndQuantity', { estimatedDeliveryDate: displayNextDeliveryDate, nextDeliveryQuantity: displayNextDeliveryQuantity })}</Text>}
            {shouldShowSubscriptionEstimatedDate && <Text color={colors.grey600}>{t('wpSubscription.estimatedDate', { estimatedDate: estimatedDeliveryDate })}</Text>}
            {shouldShowRecurringPaymentDate && <Text noWrap rootStyle={styles.estimatedShip} color={colors.grey600}>{t('wpOrder.recurringPayment', { recurringPaymentDate: chargeAt })}</Text>}
            {shouldShowMobilePaymentFailed && <Text color={colors.red400}>{t('wpOrder.PaymentFailedDescription')}</Text>}
            {shouldShowPreOrderBadge && <Text noWrap color={colors.primary600}>{t('wpOrder.preOrder')}</Text>}
            {paymentCardChipElement}
            {shouldShowRefundStatus && <RefundStatusBadge refundStatus={refundStatus} isMerchant={isMerchant} />}
          </HStack>
          <HStack gap={2} justifyContent="flex-end" flexShrink={0}>
            <Button
              variant="outlined"
              color="grey"
              rootStyle={styles.orderDetailButton}
              onClick={() => {
                if (isTransaction) {
                  onClickTransactionOrderDetail(transactionId)
                }
                else {
                  handleClickOrderDetail(orderId)
                }
              }}
            >
              {t('wpOrder.orderDetail')}
            </Button>
            {shouldShowPayButton
            && (
              <Button
                color="primary"
                onClick={() => onClickOrderAction(orderId, OrderAction.PAY, transactionId, transactionHash, displayStatus)}
              >
                {t('wpOrder.promptPayment')}
              </Button>
            )}
            {/* NOTE: campaign 再買一次目前因 store 還沒處理，先隱藏 */}
            {shouldShowRepeatButton && environment !== 'campaign'
            && (
              <Button
                color="primary"
                rootStyle={styles.orderDetailButton}
                onClick={() => onClickOrderAction(orderId, OrderAction.REPEAT, transactionId, transactionHash, displayStatus)}
                loading={isMutatingGetPaymentTransaction}
              >
                {t('wpOrder.repeatOrder')}
              </Button>
            )}
          </HStack>
        </HStack>
        {shippingSettingsElement}
      </VStack>
    </Box>
  )
}
